import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import emailService from '../../services/emailService';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { tenant } = useTenant();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });
  
  const password = watch('password');
  
  // Verify if token is valid when component loads
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token. Please request a new password reset link.');
        setSnackbarMessage('Invalid or missing reset token');
        setSnackbarOpen(true);
        setIsVerifying(false);
        return;
      }

      try {
        console.log('Verifying token:', token.substring(0, 5) + '...');
        const response = await emailService.verifyResetToken(token);
        
        if (response && response.success) {
          console.log('Token verification successful:', response);
          setTokenValidated(true);
          
          // Store the email for confirmation email later
          if (response.email) {
            setTokenEmail(response.email);
            localStorage.setItem('resetEmail', response.email);
          }
        } else {
          throw new Error('Invalid or expired token');
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        setError('Invalid or expired reset token. Please request a new password reset link.');
        setSnackbarMessage('Invalid or expired reset token');
        setSnackbarOpen(true);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);
  
  // Calculate password strength
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    
    // Length check
    if (password.length >= 8) strength += 25;
    
    // Contains number
    if (/\d/.test(password)) strength += 25;
    
    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 25;
    
    // Contains uppercase or special char
    if (/[A-Z]/.test(password) || /[^a-zA-Z0-9]/.test(password)) strength += 25;
    
    setPasswordStrength(strength);
  }, [password]);
  
  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return '#f44336'; // Red
    if (passwordStrength <= 50) return '#ff9800'; // Orange
    if (passwordStrength <= 75) return '#ffeb3b'; // Yellow
    return '#4caf50'; // Green
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Make sure password meets all requirements
      if (passwordStrength < 75) {
        setError('Password does not meet all requirements.');
        setSnackbarMessage('Password does not meet all requirements');
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }
      
      // Use the email from token validation if available
      const email = tokenEmail || localStorage.getItem('resetEmail');
      console.log('Using email for reset:', email);
      
      // Reset the password
      await resetPassword(token, data.password);
      
      // Send a confirmation email - log more details for debugging
      try {
        console.log('Sending reset confirmation to email:', email);
        
        const confirmationResponse = await fetch(`${process.env.VITE_API_URL || '/api'}/email/reset-confirmation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            resetTime: new Date().toISOString()
          })
        });
        
        const confirmationData = await confirmationResponse.json();
        console.log('Reset confirmation response:', confirmationData);
      } catch (emailError) {
        console.error('Failed to send reset confirmation email:', emailError);
        // Don't prevent success flow if only the confirmation email fails
      }
      
      // Show success message
      setSuccess(true);
      setSnackbarMessage('Password reset successful! You can now login with your new password.');
      setSnackbarOpen(true);
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Password reset failed. Please try again.';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle the case where there's no token provided
  if (!token || (isVerifying === false && !tokenValidated)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f5f7fa',
          display: 'flex',
          alignItems: 'center',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={10}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Avatar
              sx={{
                mx: 'auto',
                bgcolor: '#f44336',
                width: 70,
                height: 70,
                mb: 3,
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
              }}
            >
              <ErrorIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography variant="h5" component="h2" fontWeight="700" gutterBottom>
              Invalid Reset Link
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              The password reset link is invalid or has expired. Please request a new password reset.
            </Typography>
            <Button
              variant="contained"
              component={RouterLink}
              to="/forgot-password"
              sx={{
                py: 1.5,
                bgcolor: '#2e3f6f',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderRadius: 2,
                boxShadow: '0 5px 15px rgba(46, 63, 111, 0.2)',
                '&:hover': {
                  bgcolor: '#243252',
                },
              }}
            >
              Request New Reset Link
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }
  
  // Show loading state while verifying token
  if (isVerifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f5f7fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#2e3f6f', mb: 3 }} />
          <Typography variant="h5" fontWeight="600">
            Verifying your reset link...
          </Typography>
        </Container>
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/login"
          sx={{
            color: '#2e3f6f',
            mb: 3,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(46, 63, 111, 0.08)'
            }
          }}
        >
          Back to Login
        </Button>

        <Paper
          elevation={10}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
            borderRadius: 4,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Left Side - Branding */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 6,
              flexBasis: '40%',
              backgroundColor: '#2e3f6f',
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              backgroundImage: 'linear-gradient(135deg, rgba(46, 63, 111, 0.95) 0%, rgba(46, 63, 111, 0.8) 100%)',
              backgroundSize: 'cover',
              borderRadius: '4px 0 0 4px',
            }}
          >
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              opacity: 0.1,
              background: 'url(/abstract-pattern.svg) center/cover no-repeat',
            }} />
            {tenant?.logo ? (
              <Box
                component="img"
                src={tenant.logo}
                alt={tenant.name || 'Company Logo'}
                sx={{
                  width: 100,
                  height: 100,
                  mb: 4,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Avatar
                sx={{
                  bgcolor: 'white',
                  color: '#2e3f6f',
                  width: 84,
                  height: 84,
                  mb: 4,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  fontSize: 40,
                }}
              >
                {tenant?.name?.[0] || <RocketLaunchIcon sx={{ fontSize: 40 }} />}
              </Avatar>
            )}
            <Typography variant="h4" component="h1" fontWeight="700" gutterBottom>
              {tenant?.name || 'Transform Portal'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, maxWidth: 320 }}>
              Create a strong password to secure your account and access your personalized training experience.
            </Typography>
            
            {/* Password requirements */}
            <Box sx={{ mt: 4, width: '100%', maxWidth: 300, alignSelf: 'center' }}>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Password Requirements:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {password.length >= 8 ? 
                      <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} /> : 
                      <ErrorIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="At least 8 characters long" 
                    sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {/\d/.test(password) ? 
                      <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} /> : 
                      <ErrorIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="At least one number" 
                    sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {/[a-z]/.test(password) ? 
                      <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} /> : 
                      <ErrorIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="At least one lowercase letter" 
                    sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {(/[A-Z]/.test(password) || /[^a-zA-Z0-9]/.test(password)) ? 
                      <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} /> : 
                      <ErrorIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="At least one uppercase letter or special character" 
                    sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                  />
                </ListItem>
              </List>
            </Box>
          </Box>

          {/* Right Side - Password Reset Form */}
          <Box
            sx={{
              p: { xs: 4, sm: 6 },
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: { xs: 400, md: 600 }
            }}
          >
            {!success ? (
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      mb: 2,
                      bgcolor: '#2e3f6f',
                      width: 60,
                      height: 60,
                      boxShadow: '0 4px 12px rgba(46, 63, 111, 0.2)',
                    }}
                  >
                    <LockOutlinedIcon fontSize="large" />
                  </Avatar>
                  <Typography variant="h4" fontWeight="700" gutterBottom>
                    Reset Password
                  </Typography>
                  <Typography variant="body1" align="center" color="text.secondary">
                    Enter a new password for your account
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  margin="normal"
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    }
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1.5 }
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#2e3f6f',
                      },
                    },
                    '& label.Mui-focused': {
                      color: '#2e3f6f',
                    },
                  }}
                />

                {/* Password strength meter */}
                {password && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" fontWeight="600">
                        Password Strength
                      </Typography>
                      <Typography variant="caption" fontWeight="600" sx={{ color: getPasswordStrengthColor() }}>
                        {getPasswordStrengthLabel()}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 5, bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: 10 }}>
                      <Box
                        sx={{
                          width: `${passwordStrength}%`,
                          height: '100%',
                          bgcolor: getPasswordStrengthColor(),
                          borderRadius: 10,
                          transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                        }}
                      />
                    </Box>
                  </Box>
                )}

                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1.5 }
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#2e3f6f',
                      },
                    },
                    '& label.Mui-focused': {
                      color: '#2e3f6f',
                    },
                  }}
                />

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading || (password !== watch('confirmPassword')) || passwordStrength < 50}
                  sx={{
                    mt: 1,
                    mb: 3,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                    bgcolor: '#2e3f6f',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(46, 63, 111, 0.2)',
                    '&:hover': {
                      bgcolor: '#243357',
                      boxShadow: '0 6px 16px rgba(46, 63, 111, 0.3)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
                </Button>
              </Box>
            ) : (
              <Fade in={true} timeout={500}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Avatar
                    sx={{
                      mx: 'auto',
                      mb: 3,
                      bgcolor: '#4caf50',
                      width: 80,
                      height: 80,
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <CheckCircleIcon fontSize="large" />
                  </Avatar>
                  <Typography variant="h4" fontWeight="700" gutterBottom>
                    Password Reset Successful!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Your password has been successfully reset. You can now use your new password to log in to your account.
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/login"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontWeight: 600,
                      fontSize: '1rem',
                      bgcolor: '#2e3f6f',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(46, 63, 111, 0.2)',
                      '&:hover': {
                        bgcolor: '#243357',
                        boxShadow: '0 6px 16px rgba(46, 63, 111, 0.3)',
                      }
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
        </Paper>
      </Container>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          elevation={6}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPasswordPage;
